import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { MessageService } from 'primeng/api';
import { Institute } from '../../../models/institute/randomInstituteResponse';
import { MyInstituteService } from '../../../services/institute/myinstitute/my-institute.service';
import { MentorDeadlineDialogComponent } from '../mentor-deadline-dialog/mentor-deadline-dialog.component';
import { Store } from '@ngxs/store';
import { UserSelectors } from '../../../states/user/user-selector';
import { UserStateModel } from '../../../states/user/user-state-model';
import { ToastModule } from 'primeng/toast';
import { ChangeDetectorRef } from '@angular/core';
import { InstituteService } from '../../../services/institute/institute.service';
import { forkJoin, map, of } from 'rxjs';
import { RemoveMyInstitute } from '../../../states/myInstitutes/my-institute-actions';

@Component({
  selector: 'app-colleges-section',
  standalone: true,
  imports: [CommonModule, ButtonModule, TooltipModule, TagModule, MentorDeadlineDialogComponent, ToastModule],
  templateUrl: './colleges-section.component.html',
  styleUrl: './colleges-section.component.scss',
  providers: [MessageService]
})
export class CollegesSectionComponent implements OnInit {
  studentId: string = '';
  mentor!: UserStateModel;
  institutes: Institute[] = [];
  isLoading: boolean = true;

  /** Deadline dialog state */
  deadlineDialogVisible: boolean = false;
  selectedInstitute: Institute | null = null;

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private myInstituteService: MyInstituteService,
    private instituteService: InstituteService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.studentId = this.route.snapshot.paramMap.get('id') || '';
    this.store.select(UserSelectors.getUser).subscribe(u => (this.mentor = u));
    this.loadInstitutes();
  }

  loadInstitutes(): void {
    this.isLoading = true;
    this.myInstituteService.getMyInstitutesByid(this.studentId).subscribe({
      next: (res: any) => {
        const rawInstitutes: Institute[] = res.result || res || [];
        const enrichmentRequests = rawInstitutes.map(inst => {
          if (!inst.instituteName && inst.id) {
            return this.instituteService.getInstitute(inst.id).pipe(
              map(fullDetail => ({
                ...fullDetail,
                myInstituteId: inst.myInstituteId,
                courseId: inst.courseId,
                mentorDeadline: inst.mentorDeadline,
                inTakeYear: inst.inTakeYear,
                courseLevel: inst.courseLevel
              }))
            );
          }
          return of(inst);
        });

        if (enrichmentRequests.length > 0) {
          forkJoin(enrichmentRequests).subscribe({
            next: (enriched) => {
              this.institutes = enriched;
              this.isLoading = false;
              this.cdr.detectChanges();
            },
            error: (err) => {
              console.warn('Error enriching institutes:', err);
              this.institutes = rawInstitutes;
              this.isLoading = false;
              this.cdr.detectChanges();
            }
          });
        } else {
          this.institutes = rawInstitutes;
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        console.warn('Error loading student institutes:', err);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  openDeadlineDialog(institute: Institute): void {
    this.selectedInstitute = institute;
    this.deadlineDialogVisible = true;
  }

  onDeadlineSaved(newDeadline: string | null): void {
    if (this.selectedInstitute) {
      // Update in-place so the list reflects the change immediately
      const idx = this.institutes.findIndex(
        i => (i.myInstituteId) === (this.selectedInstitute!.myInstituteId)
      );
      if (idx !== -1) {
        this.institutes[idx] = { ...this.institutes[idx], mentorDeadline: newDeadline };
        this.institutes = [...this.institutes]; // trigger change detection
      }
    }
    this.selectedInstitute = null;
  }

  deleteCollege(institute: Institute): void {
    if (!institute.id) return;
    this.store.dispatch(new RemoveMyInstitute({ instituteId: institute.id, studentId: this.studentId })).subscribe({
      next: () => {
        this.institutes = this.institutes.filter(i => i.id !== institute.id);
        this.cdr.detectChanges();
        this.messageService.add({ severity: 'success', summary: 'Removed', detail: `${institute.instituteName || 'College'} removed from list.` });
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to remove college.' });
      }
    });
  }

  calculateDaysLeft(deadline: string | null | undefined): number | null {
    if (!deadline) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(deadline);
    if (isNaN(target.getTime())) return null;
    target.setHours(0, 0, 0, 0);
    const diff = target.getTime() - today.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }
}
